import { CheckboxTickIcon, DropDownChevronDownIcon, NavCloseIcon, ThinSearchIcon } from 'assets/icons';
import { CustomCheckbox } from 'components';
import {
  selectCurrentTeamJobPermissions,
  selectCurrentTeamTeamSettingsPermissions,
  selectUserDetails,
  showNotifier
} from 'containers/app/appSlice';
import { AssignableMember } from 'containers/jobs/jobs.model';
import { useAppDispatch, useAppSelector } from 'hooks';
import { FC, useContext, useEffect, useRef, useState } from 'react';
import {
  removeJobValidationErrors,
  selectAssignableTeamMembers,
  selectAssigneeList,
  selectInitialAssigneeList,
  selectInitialServiceArea,
  selectIsInitialisingJobFromAPI,
  selectJobValidationErrors,
  selectServiceArea,
  selectServiceAreaAssignees,
  updateAssigneeList
} from '../createJobSlice';
import { CreateEditJobNavEnum, CreateJobErrorTypes } from 'containers/jobs/constant';
import { getJobValidationErrors } from 'containers/jobs/utils';
import { CreateJobContextType } from 'containers/jobs/types';
import { Popover } from '@headlessui/react';
import { NotifierTypes } from 'constants/notifierConstants';
import If from 'components/If/If';
import { InfoOutlined } from '@mui/icons-material';

interface AssignMembersSectionProps {
  className?: string;
  context: React.Context<CreateJobContextType>;
  isEdit?: boolean;
}

const AssignMembersCheckboxSection: FC<AssignMembersSectionProps> = ({ context, isEdit = false }) => {
  const { selectedJobTab, setSelectedJobTab } = useContext<CreateJobContextType>(context);
  const [suggestionList, setSuggestionList] = useState<AssignableMember[]>([]);

  const currentUser = useAppSelector(selectUserDetails);
  const selectedJobValidations = useAppSelector(selectJobValidationErrors);
  const initialAssigneeList = useAppSelector(selectInitialAssigneeList);
  const initialServiceArea = useAppSelector(selectInitialServiceArea);
  const assigneeList = useAppSelector(selectAssigneeList);
  const serviceAreaAssignees = useAppSelector(selectServiceAreaAssignees);
  const assignableTeamMembers = useAppSelector(selectAssignableTeamMembers);
  const manageTeamPermissions = useAppSelector(selectCurrentTeamTeamSettingsPermissions);
  const selectedServiceArea = useAppSelector(selectServiceArea);
  const prevServiceAreaRef = useRef<string | null>(selectedServiceArea?.serviceAreaId ?? null);
  const { manageOthersJobs } = useAppSelector(selectCurrentTeamJobPermissions);
  const isInitialisingJobFromAPI = useAppSelector(selectIsInitialisingJobFromAPI);
  const dispatch = useAppDispatch();
  const isUpdatedToastShown = useRef(false);
  const isFilteredAssigneeToastShown = useRef(false);

  const isSingleMemberTeam = serviceAreaAssignees?.length === 1;

  useEffect(() => {
    if (isInitialisingJobFromAPI) {
      return;
    }

    if (serviceAreaAssignees?.length > 0) {
      const shouldUpdateAssigneeList =
        !assigneeList?.length || !isEdit || prevServiceAreaRef.current !== selectedServiceArea?.serviceAreaId;

      const currentUserInAssignees = serviceAreaAssignees?.find(member => member.merchantId === currentUser?.id);
      if (shouldUpdateAssigneeList) {
        if (serviceAreaAssignees.length === 1) {
          if (!manageOthersJobs) {
            currentUserInAssignees
              ? dispatch(updateAssigneeList([currentUserInAssignees]))
              : dispatch(updateAssigneeList([]));
          } else {
            dispatch(updateAssigneeList(serviceAreaAssignees));
          }
        } else {
          if (!manageOthersJobs) {
            currentUserInAssignees && dispatch(updateAssigneeList([currentUserInAssignees]));
          } else {
            dispatch(updateAssigneeList([]));
          }
        }
      }
      if (!manageOthersJobs) {
        currentUserInAssignees ? setSuggestionList([currentUserInAssignees]) : setSuggestionList([]);
      } else {
        setSuggestionList(serviceAreaAssignees);
      }
    } else if (assignableTeamMembers?.length > 0) {
      // Use assignable team members when no service areas are available
      let assignableMembersList = assignableTeamMembers;
      if (!manageOthersJobs) {
        assignableMembersList = assignableTeamMembers.filter(member => member.merchantId === currentUser?.id);
      }
      setSuggestionList(assignableMembersList);
      if (!isEdit || !assigneeList?.length) {
        if (assignableMembersList?.length === 1) {
          dispatch(updateAssigneeList([assignableMembersList[0]]));
        }
      }
    } else {
      setSuggestionList([]);
      if (!isEdit || !assigneeList?.length) {
        dispatch(updateAssigneeList([]));
      }
    }
  }, [
    serviceAreaAssignees,
    assignableTeamMembers,
    currentUser?.id,
    dispatch,
    isEdit,
    selectedServiceArea,
    isInitialisingJobFromAPI
  ]);

  const handleAssigneeListUpdation = (list: AssignableMember[]) => {
    dispatch(updateAssigneeList(list));
    dispatch(removeJobValidationErrors(CreateJobErrorTypes.ASSIGN_MEMBER));
  };

  useEffect(() => {
    const hasSameLength = initialAssigneeList?.length === assigneeList?.length;
    const allMembersMatch = assigneeList?.every(
      current => initialAssigneeList?.some(initial => initial.merchantId === current.merchantId)
      // eslint-disable-next-line function-paren-newline
    );
    const assigneesChanged = initialAssigneeList && (!hasSameLength || !allMembersMatch);
    const serviceAreaChanged = !initialServiceArea?.serviceAreaId && selectedServiceArea?.serviceAreaId;

    if (isEdit && !isUpdatedToastShown.current && serviceAreaChanged && (serviceAreaChanged || assigneesChanged)) {
      const text =
        serviceAreaChanged && assigneesChanged
          ? `Service area is updated to ${selectedServiceArea?.serviceAreaName}${
              assigneeList.length === 1
                ? ` and the job is assigned to ${assigneeList[0].name}`
                : ', please do the job assignment'
            }`
          : `Service area is updated to ${selectedServiceArea?.serviceAreaName}`;

      isUpdatedToastShown.current = true;
      dispatch(
        showNotifier({
          message: {
            primaryMessage: text
          },
          type: NotifierTypes.INFO,
          showClose: false
        })
      );
    }
  }, [assigneeList, isEdit]);

  useEffect(() => {
    if (
      isEdit &&
      !isFilteredAssigneeToastShown.current &&
      manageOthersJobs &&
      initialAssigneeList?.length &&
      serviceAreaAssignees?.length > 0
    ) {
      const filteredAssigneeList = initialAssigneeList?.filter(
        member => suggestionList?.some(listMember => listMember.merchantId === member.merchantId)
        // eslint-disable-next-line function-paren-newline
      );
      const removedAssigneeList = initialAssigneeList
        ?.filter(member => !suggestionList?.some(listMember => listMember.merchantId === member.merchantId))
        .map(member => member.name);

      isFilteredAssigneeToastShown.current = true;
      if (removedAssigneeList?.length) {
        dispatch(updateAssigneeList(filteredAssigneeList));
        const text = `Job assignee updated, as ${removedAssigneeList.join(', ')} ${
          removedAssigneeList.length > 1 ? 'are' : 'is'
        } not available in this service area`;
        dispatch(
          showNotifier({
            message: {
              primaryMessage: text
            },
            type: NotifierTypes.INFO,
            showClose: false
          })
        );
      }
    }
  }, [manageOthersJobs, initialAssigneeList, suggestionList, isEdit]);

  const handleMemberSelection = (member: AssignableMember) => {
    const isAlreadySelected = assigneeList?.some(selected => selected.merchantId === member.merchantId);
    const newList = isAlreadySelected
      ? assigneeList.filter(selected => selected.merchantId !== member.merchantId)
      : [...assigneeList, member];
    handleAssigneeListUpdation(newList);

    if (selectedJobTab !== CreateEditJobNavEnum.JOB_SCHEDULE) {
      setSelectedJobTab(CreateEditJobNavEnum.JOB_SCHEDULE);
    }
  };

  const handleRemoveMember = (e: React.MouseEvent, memberId: string) => {
    e.stopPropagation();
    if (!manageOthersJobs) {
      const isNotSelf = memberId !== currentUser?.id;
      if (isNotSelf) {
        dispatch(
          showNotifier({
            message: {
              primaryMessage: 'You do not have permission to remove this member'
            },
            type: NotifierTypes.INFO,
            showClose: false,
            fontStyle: 'text-primary font-normal'
          })
        );
        return;
      }
    }
    const newList = assigneeList.filter(member => member.merchantId !== memberId);
    handleAssigneeListUpdation(newList);
  };

  const isCurrentUserInServiceArea =
    serviceAreaAssignees?.some(member => member.merchantId === currentUser?.id) ?? false;

  const assigneeError = getJobValidationErrors(selectedJobValidations, CreateJobErrorTypes.ASSIGN_MEMBER);

  const getEmptyStateText = () => {
    if (!manageOthersJobs) {
      if (serviceAreaAssignees?.length > 0 && !isCurrentUserInServiceArea) {
        const msg = (
          <span className="flex items-center gap-2">
            <InfoOutlined className="h-4 w-4" /> You must be in the service area to assign yourself to this job.
          </span>
        );
        return msg;
      }
    }
    return 'No members found!';
  };

  return (
    <div className="w-full">
      <div className="pb-2 text-17px font-semibold text-heading">Assign team members</div>
      {assigneeError && <div className="text-sbase font-semibold text-error">{assigneeError}</div>}
      {isCurrentUserInServiceArea && (
        <div className="flex items-center gap-2.5 pt-2">
          <CustomCheckbox
            id="assign-job-to-self"
            onClick={() => {
              const currentUserMember = suggestionList?.find(member => member.merchantId === currentUser?.id);
              if (currentUserMember) {
                handleMemberSelection(currentUserMember);
              }
            }}
            isChecked={assigneeList?.some(member => member.merchantId === currentUser?.id)}
            className={isSingleMemberTeam ? 'cursor-default' : ''}>
            {assigneeList?.some(member => member.merchantId === currentUser?.id) && <CheckboxTickIcon />}
          </CustomCheckbox>
          <label htmlFor="assign-job-to-self" className="text-sbase text-secondary">
            Assign to myself
          </label>
        </div>
      )}

      {(manageTeamPermissions.viewExistingMember || manageTeamPermissions.editExistingMember) && (
        <div className={`${isCurrentUserInServiceArea ? 'mt-5' : 'mt-1'}`}>
          <Popover className="relative">
            {({ open }) => (
              <>
                <Popover.Button className="w-full">
                  <div
                    className="relative flex w-full items-center justify-between 
                      rounded-md border border-borderGray bg-white p-3">
                    <div className="relative flex grow items-start">
                      <div className="flex h-[26px] items-center justify-center">
                        <ThinSearchIcon />
                      </div>

                      <div className="flex flex-wrap gap-2.5 px-3">
                        {assigneeList?.map(member => (
                          <div
                            key={member.merchantId}
                            className="flex items-center gap-2 rounded-[10px] bg-borderGray py-1 px-2">
                            <div className="text-sbase text-primaryText">
                              {member.name || ''}
                              {member.merchantId === currentUser?.id && <span> {'(you)'}</span>}
                            </div>
                            <NavCloseIcon
                              className="path-fill-current path cursor-pointer text-gray-1"
                              width={10}
                              height={10}
                              onClick={e => {
                                e.stopPropagation();
                                handleRemoveMember(e, member.merchantId);
                              }}
                            />
                          </div>
                        ))}
                        {assigneeList?.length === 0 && (
                          <div className="pt-1 text-sbase text-primaryText">
                            Assign team members
                            <If condition={manageOthersJobs}>
                              {serviceAreaAssignees?.length > 0 &&
                                ` (${serviceAreaAssignees.length} working in ${selectedServiceArea?.serviceAreaName})`}
                              {!serviceAreaAssignees?.length &&
                                assignableTeamMembers?.length > 0 &&
                                ` (${assignableTeamMembers.length} working)`}
                            </If>
                          </div>
                        )}
                      </div>
                    </div>
                    <DropDownChevronDownIcon className="h-[8px] w-[14px] shrink-0 text-gray-400" />
                  </div>
                </Popover.Button>

                <Popover.Panel
                  className="absolute z-10 mt-1 w-full rounded-md border
                 border-borderGray bg-white p-2 shadow-lg">
                  <div className="max-h-48 overflow-y-auto">
                    {suggestionList?.length > 0 ? (
                      suggestionList.map(member => (
                        <div
                          key={member?.merchantId}
                          className="mb-2 flex cursor-pointer items-center px-2 py-1 hover:bg-gray-50"
                          onClick={() => handleMemberSelection(member)}>
                          <CustomCheckbox
                            id={member?.merchantId ?? ''}
                            isChecked={assigneeList?.some(selected => selected.merchantId === member?.merchantId)}
                            onClick={() => handleMemberSelection(member)}
                            className="h-4 w-4">
                            {assigneeList?.some(selected => selected.merchantId === member?.merchantId) && (
                              <CheckboxTickIcon />
                            )}
                          </CustomCheckbox>
                          <label htmlFor={member?.merchantId} className="ml-3 flex cursor-pointer items-center gap-2">
                            <span className="text-sbase text-primaryText">{member?.name || ''}</span>
                          </label>
                        </div>
                      ))
                    ) : (
                      <div className="my-4 ml-5 text-sbase font-semibold text-primaryText">{getEmptyStateText()}</div>
                    )}
                  </div>
                </Popover.Panel>
              </>
            )}
          </Popover>
        </div>
      )}
    </div>
  );
};

export default AssignMembersCheckboxSection;
